import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  createFileRoute,
  Outlet,
  useMatch,
  useParams,
} from "@tanstack/react-router";
import { useAtom } from "jotai";
import { z } from "zod";
import { TabNav } from "~/components/TabNav";
import { layerSnapshotSchema } from "~/components/map/registry/layerSnapshot";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { RentAnalysisMapWrapper } from "~/routes/(restricted)/ejendomstjek/-components/aside/RentAnalysisMapWrapper";
import { SalesPricesMapWrapper } from "~/routes/(restricted)/ejendomstjek/-components/aside/SalesPricesMapWrapper";
import { Header } from "./-components/Header";
import { PaywallDialog } from "./-components/PaywallDialog";
import {
  paywallOpenAtom,
  usePaywallNavigationRestrictions,
} from "./-state/paywall-atoms";
import { searchSchema } from "./rent-analysis/-schemas/rent-analysis-search";
import { RESTRICTION_LAYER_REGISTRY } from "./restrictions/-components/restrictionsLayersConfig";

export const Route = createFileRoute(
  "/(restricted)/ejendomstjek/address/$accessAddressId/{-$placeId}",
)({
  component: RouteComponent,
  validateSearch: searchSchema.extend({
    session_id: z.string().optional(),
    mapLayers: layerSnapshotSchema(RESTRICTION_LAYER_REGISTRY.layers),
  }),
  loader: async ({ context: { i18n } }) => {
    await i18n.loadNamespaces("property-check");

    const title = i18n.t("property-check:meta.title");

    return { meta: { title } };
  },
  head: async (context) => {
    return {
      meta: [
        {
          title: context.loaderData?.meta.title,
        },
      ],
    };
  },
});

function RouteComponent() {
  const { accessAddressId, placeId } = Route.useParams();
  // A selection only goes to the section that can interpret it: unitId is a
  // unit, bfeNumber a BFE. See docs/adr/0002-sales-prices-keyed-by-bfe.md.
  const { unitId, bfeNumber } = useParams({ strict: false });

  const { flags } = useFeatureFlags();

  const { t } = useTranslation(["property-check"]);
  const [paywallOpen, setPaywallOpen] = useAtom(paywallOpenAtom);
  const navigationRestrictions = usePaywallNavigationRestrictions();

  useEffect(() => {
    return () => setPaywallOpen(false);
  }, [setPaywallOpen]);

  const isOverview = useMatch({
    from: "/(restricted)/ejendomstjek/address/$accessAddressId/{-$placeId}/overview/{-$unitId}",
    shouldThrow: false,
  });

  const isRestrictions = useMatch({
    from: "/(restricted)/ejendomstjek/address/$accessAddressId/{-$placeId}/restrictions/{-$currentPlaceId}",
    shouldThrow: false,
  });

  const isSalesPrices = useMatch({
    from: "/(restricted)/ejendomstjek/address/$accessAddressId/{-$placeId}/sales-prices/{-$bfeNumber}",
    shouldThrow: false,
  });

  const showAside = !isOverview && !isRestrictions;

  return (
    <div className="min-h-full w-full self-start">
      <header className="sticky top-0 z-10 -mx-20 bg-white px-20 opacity-95">
        <div className="flex flex-col gap-2 pt-4 md:pt-6">
          <Header
            accessAddressId={accessAddressId}
            placeId={placeId}
          />
          <TabNav>
            <TabNav.Item
              to="/ejendomstjek/address/$accessAddressId/{-$placeId}/overview/{-$unitId}"
              params={{
                accessAddressId,
                placeId,
                unitId,
              }}
              search={(current) => current}
              activeOptions={{
                exact: true, // The overview tab is a level above the others, so the match needs to be exact
                includeSearch: false, // overview tab should still be active even though there are search params
              }}
            >
              {t("property-check:header.tabs.overview")}
            </TabNav.Item>
            <TabNav.Item
              navigationRestrictions={navigationRestrictions}
              to="/ejendomstjek/address/$accessAddressId/{-$placeId}/rent-analysis/{-$unitId}"
              params={{
                accessAddressId,
                placeId,
                unitId,
              }}
              search={(current) => current}
            >
              {t("property-check:header.tabs.rent_analysis")}
            </TabNav.Item>
            <TabNav.Item
              navigationRestrictions={navigationRestrictions}
              to="/ejendomstjek/address/$accessAddressId/{-$placeId}/renting-out/{-$unitId}"
              params={{
                accessAddressId,
                placeId,
                unitId,
              }}
              search={(current) => current}
            >
              {t("property-check:header.tabs.renting_out")}
            </TabNav.Item>
            <TabNav.Item
              navigationRestrictions={navigationRestrictions}
              to="/ejendomstjek/address/$accessAddressId/{-$placeId}/restrictions/{-$currentPlaceId}"
              params={{
                accessAddressId,
                placeId,
              }}
              search={(current) => current}
            >
              {t("property-check:header.tabs.restrictions")}
            </TabNav.Item>
            {flags.SALES_PRICES_ENABLED && (
              <TabNav.Item
                navigationRestrictions={navigationRestrictions}
                to="/ejendomstjek/address/$accessAddressId/{-$placeId}/sales-prices/{-$bfeNumber}"
                params={{
                  accessAddressId,
                  placeId,
                  bfeNumber,
                }}
                search={(current) => current}
              >
                {t("property-check:header.tabs.sales_prices")}
              </TabNav.Item>
            )}
          </TabNav>
        </div>
      </header>

      <PaywallDialog
        open={paywallOpen}
        onOpenChange={setPaywallOpen}
      />

      <main className="flex flex-col gap-6 py-6 lg:flex-row lg:items-start">
        <div className={showAside ? "lg:flex-1" : "w-full"}>
          <Outlet />
        </div>
        {showAside && (
          <div className="3xl:w-[650px] lg:sticky lg:top-[131px] lg:w-[450px]">
            {isSalesPrices ? (
              <SalesPricesMapWrapper />
            ) : (
              <RentAnalysisMapWrapper />
            )}
          </div>
        )}
      </main>
    </div>
  );
}
